import { FC, useState, Children, isValidElement, ReactElement, Dispatch, SetStateAction } from 'react';
import './Tabs.css';
import ButtonGroup from '../ButtonGroup/ButtonGroup';

interface TabsProps {
  children: React.ReactNode;
  activeTab?: string;
  onTabChange?: Dispatch<SetStateAction<string>>;
}

const Tabs: FC<TabsProps> = ({ children, activeTab, onTabChange }) => {
  const [internalActiveTab, setInternalActiveTab] = useState<string>(() => {
    const validChildren = Children.toArray(children).filter(child => 
      isValidElement(child) && 'id' in (child as ReactElement<any>).props
    );
    return validChildren.length > 0 && isValidElement(validChildren[0]) 
      ? (validChildren[0] as ReactElement<any>).props.id 
      : '';
  });
  
  // Use either controlled or uncontrolled state
  const currentActiveTab = activeTab !== undefined ? activeTab : internalActiveTab;
  const handleTabChange = (tabId: string | string[]) => {
    // We only handle single tab selection, so we can safely cast to string
    const newTabId = typeof tabId === 'string' ? tabId : tabId[0];
    
    if (onTabChange) {
      onTabChange(newTabId);
    } else {
      setInternalActiveTab(newTabId);
    }
  };
  
  const tabOptions = Children.map(children, (child) => {
    if (!isValidElement(child) || !('id' in (child as ReactElement<any>).props)) return null;
    const typedChild = child as ReactElement<any>;
    return {
      value: typedChild.props.id,
      label: typedChild.props.title
    };
  })?.filter((option): option is { value: string; label: string } => option !== null) ?? [];

  return (
    <div className="tabs">
      <ButtonGroup
        options={tabOptions}
        value={currentActiveTab}
        onChange={handleTabChange}
        className="tabs__navigation"
        gap="xs"
      />
      <div className="tabs__content">
        {Children.map(children, (child) => {
          if (!isValidElement(child) || !('id' in (child as ReactElement<any>).props)) return null;
          const typedChild = child as ReactElement<any>;
          if (typedChild.props.id !== currentActiveTab) return null;
          return typedChild;
        })}
      </div>
    </div>
  );
};

export default Tabs; 
